<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
    overlay-opacity="0.85"
    content-class="modern-editor-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3"
            >mdi mdi-information-outline</v-icon
          >
          Edit What We Do
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Help text -->
      <v-card-text class="px-6 pt-5 pb-1 text-body-1 text--secondary">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
          Describe how your agency accomplishes its mission through programs and
          activities...
        </div>
      </v-card-text>

      <!-- Error/Success message with smooth transition -->
      <v-expand-transition>
        <v-card-text class="px-6 py-0" v-if="message">
          <v-alert
            :type="messageType"
            class="mb-4 elevation-2"
            transition="scale-transition"
            :icon="
              messageType === 'success'
                ? 'mdi-check-circle'
                : 'mdi-alert-circle'
            "
          >
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-expand-transition>

      <!-- Main content area -->
      <v-card-text class="px-6 py-2">
        <div
          class="text-subtitle-1 font-weight-medium primary--text mb-3 d-flex align-center"
        >
          <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
          Our Programs & Activities
        </div>

        <v-textarea
          v-model="profile.what_we_do"
          outlined
          auto-grow
          rows="5"
          placeholder="Describe your impactful programs and activities..."
          class="modern-editor-textarea"
          :rules="[rules.required]"
          counter="600"
          maxlength="600"
          @focus="onChange"
          no-resize
        >
          <template v-slot:label>
            <span class="text--secondary">Detailed description</span>
          </template>
        </v-textarea>

        <div class="text-caption text-right grey--text mt-1">
          {{ profile.what_we_do ? profile.what_we_do.length : 0 }}/600
          characters
        </div>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-arrow-left</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onSave"
          depressed
          width="160"
          :loading="loading"
          :disabled="!profile.what_we_do"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-content-save</v-icon>
          Save Changes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
export default {
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      messageType: "error",
      loading: false,
      rules: {
        required: value => !!value || "This field is required"
      },
      profile: {}
    };
  },
  methods: {
    ...mapActions("auth", {
      updateProfile: "updateProfile"
    }),
    onChange() {
      this.message = null;
    },
    async onSave() {
      if (!this.profile.what_we_do) {
        this.message =
          "Please provide details about your programs and activities";
        this.messageType = "error";
        return;
      }

      this.loading = true;
      try {
        await this.updateProfile({ what_we_do: this.profile.what_we_do });
        this.message = "Your description was saved successfully!";
        this.messageType = "success";
        setTimeout(() => {
          this.onClose();
        }, 1500);
      } catch (error) {
        this.message =
          error.response?.data?.message || "An error occurred while saving";
        this.messageType = "error";
      } finally {
        this.loading = false;
      }
    }
  },
  computed: {
    ...mapState("auth", {
      myProfile: "profile"
    })
  },
  watch: {
    myProfile(newValue) {
      this.profile = { ...newValue };
    }
  },
  mounted() {
    this.profile = { ...this.myProfile };
    this.message = null;
  }
};
</script>

<style scoped>
.modern-editor-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.modern-editor-textarea::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 2px solid #e0e0e0; */
  background-color: #f9f9f9 !important;
}

.modern-editor-textarea::v-deep .v-input__slot:hover {
  /* border-color: var(--v-primary-base); */
  background-color: white !important;
}

.modern-editor-textarea::v-deep .v-text-field__slot textarea {
  padding: 14px;
  line-height: 1.7 !important;
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.v-card {
  overflow: hidden;
}

/* Scrollbar styling */
.modern-editor-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-editor-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-editor-dialog::-webkit-scrollbar-thumb {
     background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-editor-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}
</style>
